import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { Grid, Tab, Tabs } from "@mui/material";
import { FormikSaveBar } from "../../../utils/FormikSaveBar";
import { PMField } from "../../../utils/PMField";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PMSwitch from "../../../utils/PMSwitch";
import {
  createOfferCalculationAsync,
  updateOfferCalculationAsync,
  getOfferCalculationAsync,
} from "../../../../slices/offer-calculation";
import { mapErrors } from "../../../Utils";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import OfferCalculationItems from "./calculation-items";
import OfferCalculationModuleBuilder, { OfferCalculationUserInsertView } from "./module-builder";

const CALCULATION_TYPES = [
  { value: "standard", label: "Standard" },
  { value: "product", label: "Produkt" },
  { value: "hours", label: "Timer" },
  { value: "car_allowance", label: "Bilgodtgjørelse" },
  { value: "nested", label: "Nestet kalkyle" },
];

const CALCULATION_MODES = {
  lines: "lines",
  modules: "modules",
};

export const OfferCalculationInfo = (props) => {
  const { t } = useTranslation();
  const { calculation, isLoading } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeModuleTab, setActiveModuleTab] = useState("modules");

  const updateOrCreateCalculation = (values) => {
    if (values.id) {
      return dispatch(updateOfferCalculationAsync(values));
    } else {
      return dispatch(createOfferCalculationAsync(values));
    }
  };


  if (isLoading && calculation?.id) {
    return <ResourceLoading />;
  }

  return (
    <>
      <Formik
        initialValues={{
          id: calculation?.id,
          title: calculation?.title || "",
          description: calculation?.description || "",
          calculation_type: calculation?.calculation_type || "standard",
          active: calculation?.active !== undefined ? calculation?.active : true,
          items: calculation?.items || [],
          nested_calculations: calculation?.nested_calculations || [],
          show_hours: calculation?.show_hours !== undefined ? calculation?.show_hours : true,
          show_quantities: calculation?.show_quantities !== undefined ? calculation?.show_quantities : true,
          show_products: calculation?.show_products !== undefined ? calculation?.show_products : true,
          module_enabled: calculation?.module_enabled || false,
          module_graph: calculation?.module_graph || null,
          module_result: calculation?.module_result || null,
        }}
        enableReinitialize={true}
        validationSchema={Yup.object({
          title: Yup.string().required("Tittel er påkrevd"),
        })}
        onSubmit={async (values, helpers) => {
          // Store current values to restore if save fails
          const currentValues = { ...values };
          
          try {
            helpers.setSubmitting(true);
            const result = await updateOrCreateCalculation(values);
            
            // Check if the action was rejected
            if (result.type?.endsWith('/rejected')) {
              const errorPayload = result.payload || result.error;
              const errorMessage = errorPayload?.message || 
                                 (errorPayload?.errors ? mapErrors(errorPayload.errors) : null) ||
                                 (errorPayload?.response?.data?.errors ? mapErrors(errorPayload.response.data.errors) : null) ||
                                 t("Noe gikk galt. Kunne ikke lagre data!");
              toast.error(errorMessage);
              helpers.setStatus({ success: false });
              helpers.setErrors({ submit: errorMessage });
              helpers.setSubmitting(false);
              // Restore values - don't reset form
              Object.keys(currentValues).forEach(key => {
                helpers.setFieldValue(key, currentValues[key]);
              });
              return;
            }
            
            // Check for errors in the response payload
            const response = result.payload?.response || result.payload;
            
            if (response?.status === 500 || response?.status === 400) {
              const errors = response?.data?.errors || result.payload?.errors;
              if (errors) {
                const errorMessage = Array.isArray(errors) ? mapErrors(errors) : (typeof errors === 'string' ? errors : JSON.stringify(errors));
                toast.error(errorMessage || t("Noe gikk galt. Kunne ikke lagre data!"));
                helpers.setStatus({ success: false });
                helpers.setErrors({ submit: errorMessage });
                helpers.setSubmitting(false);
                // Restore values - don't reset form
                Object.keys(currentValues).forEach(key => {
                  helpers.setFieldValue(key, currentValues[key]);
                });
                return;
              }
              throw new Error("Server error");
            }
            
            // Check if payload has errors array
            if (result.payload?.errors && Array.isArray(result.payload.errors) && result.payload.errors.length > 0) {
              const errorMessage = mapErrors(result.payload.errors);
              toast.error(errorMessage || t("Noe gikk galt. Kunne ikke lagre data!"));
              helpers.setStatus({ success: false });
              helpers.setErrors({ submit: errorMessage });
              helpers.setSubmitting(false);
              // Restore values - don't reset form
              Object.keys(currentValues).forEach(key => {
                helpers.setFieldValue(key, currentValues[key]);
              });
              return;
            }
            
            // Success - only proceed if we got valid data back
            // API returns the calculation directly, not wrapped in data
            const savedCalculation = result.payload?.data || result.payload;
            if (!savedCalculation || (response?.status && response.status >= 400)) {
              throw new Error("Lagring feilet");
            }
            
            // Extract ID from saved calculation
            const calculationId = savedCalculation?.id || savedCalculation?.data?.id;
            
            if (!calculationId) {
              console.error("No ID in saved calculation:", savedCalculation);
              throw new Error("Kunne ikke hente ID fra lagret kalkyle");
            }
            
            toast.success(t("Data ble lagret!"));
            helpers.setStatus({ success: true });
            helpers.setSubmitting(false);
            
            // If this was a new calculation and it was successfully created, navigate to the edit page
            if (!values.id && calculationId) {
              // Update form values with the new ID first
              helpers.setFieldValue("id", calculationId);
              
              // Fetch the full calculation to ensure we have all data, then navigate
              dispatch(getOfferCalculationAsync(calculationId)).then(() => {
                // Small delay to ensure state is updated before navigation
                setTimeout(() => {
                  navigate(`/dashboard/offers/calculations/${calculationId}`, { replace: true });
                }, 100);
              }).catch((err) => {
                console.error("Error fetching calculation after create:", err);
                // Still navigate even if fetch fails
                navigate(`/dashboard/offers/calculations/${calculationId}`, { replace: true });
              });
            } else if (values.id) {
              // For updates, refresh the calculation data
              dispatch(getOfferCalculationAsync(values.id));
            }
          } catch (err) {
            helpers.setStatus({ success: false });
            const errorMessage = err.message || t("Noe gikk galt. Kunne ikke lagre data!");
            helpers.setErrors({ submit: errorMessage });
            helpers.setSubmitting(false);
            toast.error(errorMessage);
            // Restore values - don't reset form on error
            Object.keys(currentValues).forEach(key => {
              helpers.setFieldValue(key, currentValues[key]);
            });
          }
        }}
      >
        {(formik) => {
          const calculationMode = formik.values.module_enabled ? CALCULATION_MODES.modules : CALCULATION_MODES.lines;
          const canChangeMode = !formik.values.id;

          const setCalculationMode = (mode) => {
            if (!canChangeMode) return;
            const useModuleBuilder = mode === CALCULATION_MODES.modules;
            formik.setFieldValue("module_enabled", useModuleBuilder);
            if (!useModuleBuilder) {
              formik.setFieldValue("module_graph", null);
              formik.setFieldValue("module_result", null);
            }
          };

          return (
          <Form>
            <Grid item xs={12}>
              <div className="shadow sm:overflow-auto sm:rounded-md">
                <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                      {t("Kalkyle informasjon")}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500"></p>
                  </div>

                  <div className="grid grid-cols-6 gap-6">
                    <div className="col-span-6">
                      <div className="offer-calculation-mode-picker">
                        <div>
                          <h4>{t("offer.calculation.mode.title")}</h4>
                          <p>{t("offer.calculation.mode.subtitle")}</p>
                        </div>
                        <div className="offer-calculation-mode-picker__options">
                          <button
                            className={[
                              "offer-calculation-mode-picker__option",
                              calculationMode === CALCULATION_MODES.lines ? "is-active" : "",
                            ].join(" ")}
                            disabled={!canChangeMode}
                            type="button"
                            onClick={() => setCalculationMode(CALCULATION_MODES.lines)}
                          >
                            <strong>{t("offer.calculation.mode.lines_title")}</strong>
                            <span>{t("offer.calculation.mode.lines_hint")}</span>
                          </button>
                          <button
                            className={[
                              "offer-calculation-mode-picker__option",
                              calculationMode === CALCULATION_MODES.modules ? "is-active" : "",
                            ].join(" ")}
                            disabled={!canChangeMode}
                            type="button"
                            onClick={() => setCalculationMode(CALCULATION_MODES.modules)}
                          >
                            <strong>{t("offer.calculation.mode.modules_title")}</strong>
                            <span>{t("offer.calculation.mode.modules_hint")}</span>
                          </button>
                        </div>
                        {!canChangeMode ? (
                          <p className="offer-calculation-mode-picker__locked">
                            {t("offer.calculation.mode.locked_hint")}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div className="col-span-6 sm:col-span-6">
                      <PMField
                        name="title"
                        type="text"
                        label={t("Tittel")}
                        required
                        {...formik}
                      />
                    </div>

                    {calculationMode === CALCULATION_MODES.lines ? (
                      <div className="col-span-6 sm:col-span-3">
                        <PMField
                          name="calculation_type"
                          type="select"
                          label={t("Type")}
                          options={CALCULATION_TYPES}
                          {...formik}
                        />
                      </div>
                    ) : null}

                    <div className="col-span-6 sm:col-span-3">
                      <PMSwitch label="Aktiv" name="active" {...formik} />
                    </div>

                    <div className="col-span-6">
                      <PMField
                        name="description"
                        type="text"
                        label="Beskrivelse"
                        multiline
                        rows={3}
                        {...formik}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Grid>

            <Grid item xs={12} sx={{ mt: 3 }}>
              <div className="shadow sm:rounded-md">
                <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                      {t("offer.calculation.builder.section_title")}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {calculationMode === CALCULATION_MODES.modules
                        ? t("offer.calculation.builder.section_hint_modules")
                        : t("offer.calculation.builder.section_hint_lines")}
                    </p>
                  </div>

                  {calculationMode === CALCULATION_MODES.modules ? (
                    <Tabs
                      value={activeModuleTab}
                      onChange={(event, value) => setActiveModuleTab(value)}
                      textColor="primary"
                      indicatorColor="primary"
                    >
                      <Tab label={t("offer.calculation.builder.tab_modules")} value="modules" />
                      <Tab label={t("offer.calculation.builder.tab_insert")} value="insert" />
                    </Tabs>
                  ) : null}

                  <div className="pt-2">
                    {calculationMode === CALCULATION_MODES.lines ? (
                      <OfferCalculationItems
                        name="items"
                        calculationType={formik.values.calculation_type}
                        calculationId={formik.values.id}
                        {...formik}
                      />
                    ) : activeModuleTab === "modules" ? (
                      <OfferCalculationModuleBuilder {...formik} />
                    ) : (
                      <OfferCalculationUserInsertView {...formik} />
                    )}
                  </div>
                </div>
              </div>
            </Grid>

            <FormikSaveBar {...formik} />
          </Form>
          );
        }}
      </Formik>
    </>
  );
};

